/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { EpicAcceptanceSummary } from '@shared/types/acceptance-reports';
import { StorySummaryRow } from './StorySummaryRow';

interface EpicAccordionProps {
  epic: EpicAcceptanceSummary;
  defaultExpanded?: boolean;
  onStoryClick?: (storyId: string, epicId: string) => void;
}

const STATUS_COLORS = {
  accepted: '#10b981',
  readyForReview: '#f59e0b',
  scoped: '#8b5cf6',
  required: '#6b7280',
};

export function EpicAccordion({ epic, defaultExpanded = false, onStoryClick }: EpicAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const completionPercentage = epic.totals.total > 0
    ? Math.round((epic.totals.accepted / epic.totals.total) * 100)
    : 0;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        marginBottom: '8px',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={toggleExpanded}
        style={{
          display: 'grid',
          gridTemplateColumns: '24px 1fr repeat(4, 60px) 60px',
          alignItems: 'center',
          padding: '10px 12px',
          backgroundColor: '#fff',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fff';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            {epic.epicId}
          </span>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>
            {epic.epicTitle}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: '#9ca3af',
              backgroundColor: '#f3f4f6',
              padding: '2px 6px',
              borderRadius: '10px',
            }}
          >
            {epic.stories.length} {epic.stories.length === 1 ? 'story' : 'stories'}
          </span>
        </div>

        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            color: epic.totals.accepted > 0 ? STATUS_COLORS.accepted : '#ccc',
            fontSize: '13px',
          }}
        >
          {epic.totals.accepted}
        </div>

        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            color: epic.totals.readyForReview > 0 ? STATUS_COLORS.readyForReview : '#ccc',
            fontSize: '13px',
          }}
        >
          {epic.totals.readyForReview}
        </div>

        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            color: epic.totals.scoped > 0 ? STATUS_COLORS.scoped : '#ccc',
            fontSize: '13px',
          }}
        >
          {epic.totals.scoped}
        </div>

        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            color: epic.totals.required > 0 ? STATUS_COLORS.required : '#ccc',
            fontSize: '13px',
          }}
        >
          {epic.totals.required}
        </div>

        <div
          style={{
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 500,
            color: completionPercentage === 100 ? STATUS_COLORS.accepted : '#6b7280',
          }}
        >
          {completionPercentage}%
        </div>
      </div>

      {isExpanded && (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr repeat(4, 60px) 32px',
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              fontSize: '11px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <div>Story</div>
            <div style={{ textAlign: 'center', color: STATUS_COLORS.accepted }}>Acc</div>
            <div style={{ textAlign: 'center', color: STATUS_COLORS.readyForReview }}>Rev</div>
            <div style={{ textAlign: 'center', color: STATUS_COLORS.scoped }}>Scp</div>
            <div style={{ textAlign: 'center', color: STATUS_COLORS.required }}>Req</div>
            <div></div>
          </div>
          {epic.stories.map((story) => (
            <StorySummaryRow
              key={story.storyId}
              story={story}
              onStoryClick={onStoryClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
